import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/AppContext";

function quoteWaLink(settings, f) {
  const number = settings?.business?.default_whatsapp || "919849966082";
  const lines = [
    "*New Quote Request — AnVibes Exports*", "",
    `Name: ${f.name}`, f.company && `Company: ${f.company}`, `Country: ${f.country}`,
    `Email: ${f.email}`, f.phone && `Phone: ${f.phone}`, f.whatsapp && `WhatsApp: ${f.whatsapp}`,
    f.product_category && `Category: ${f.product_category}`, f.product && `Product: ${f.product}`,
    f.quantity && `Quantity: ${f.quantity}`, f.packaging && `Packaging: ${f.packaging}`,
    f.destination_port && `Destination Port: ${f.destination_port}`, f.target_date && `Target Date: ${f.target_date}`,
    f.message && `Message: ${f.message}`,
  ].filter(Boolean);
  return `https://wa.me/${number}?text=${encodeURIComponent(lines.join("\n"))}`;
}

const CATEGORIES = [
  { value: "rice", label: "Rice" },
  { value: "spices", label: "Indian Spices" },
  { value: "tea", label: "Tea" },
];

const EMPTY = {
  name: "", company: "", country: "", email: "", phone: "", whatsapp: "",
  product_category: "", product: "", quantity: "", packaging: "",
  destination_port: "", target_date: "", message: "", consent: false, website: "",
};

const inputCls = "w-full border border-forest/15 bg-white px-4 py-3 text-sm text-forest placeholder:text-forest/35 focus:border-gold focus:outline-none transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.22em] text-forest/60 mb-1.5";

export default function QuoteForm({ defaultCategory = "", defaultProduct = "", dark = false }) {
  const [params] = useSearchParams();
  const { settings } = useSettings();
  const [form, setForm] = useState({ ...EMPTY });
  const [products, setProducts] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const qp = params.get("product");
    const qc = params.get("category");
    setForm((f) => ({
      ...f,
      product: qp || defaultProduct || f.product,
      product_category: qc || defaultCategory || f.product_category,
    }));
  }, [params, defaultCategory, defaultProduct]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const filtered = form.product_category ? products.filter((p) => p.category === form.product_category) : products;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/inquiries", form);
      setSent({ ...form });
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="border border-gold/40 bg-forest/5 p-10 text-center" data-testid="quote-success-message">
        <CheckCircle2 className="mx-auto h-12 w-12 text-gold-dark" />
        <h3 className="mt-5 font-serif text-2xl text-forest">Inquiry Received</h3>
        <p className="mt-3 text-sm text-[#4A554D] leading-relaxed max-w-md mx-auto">
          Thank you for your inquiry. Our export team will review your requirements and contact you shortly.
        </p>
        <div className="mt-7 border border-gold/30 bg-white p-6 max-w-md mx-auto">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-dark">Faster response</p>
          <p className="mt-2 text-sm text-forest">Send the same inquiry to our export desk on WhatsApp for a quicker reply.</p>
          <a
            href={quoteWaLink(settings, sent)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="quote-send-whatsapp-button"
            className="mt-4 inline-flex items-center gap-2.5 bg-[#1E4B33] px-6 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-parchment hover:bg-forest transition-colors"
          >
            <MessageCircle className="h-4 w-4" /> Send on WhatsApp
          </a>
        </div>
        <button
          data-testid="quote-send-another-button"
          onClick={() => { setSent(null); setForm({ ...EMPTY }); }}
          className="mt-6 border border-forest/25 px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-forest hover:border-gold hover:text-gold-dark transition-colors"
        >
          Send Another Inquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="quote-form">
      <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div>
        <label className={labelCls} htmlFor="qf-name">Full Name *</label>
        <input id="qf-name" data-testid="quote-field-name" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
      </div>
      <div>
        <label className={labelCls} htmlFor="qf-company">Company Name</label>
        <input id="qf-company" data-testid="quote-field-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Company / business name" />
      </div>
      <div>
        <label className={labelCls} htmlFor="qf-country">Country *</label>
        <input id="qf-country" data-testid="quote-field-country" required className={inputCls} value={form.country} onChange={set("country")} placeholder="Destination country" />
      </div>
      <div>
        <label className={labelCls} htmlFor="qf-email">Email *</label>
        <input id="qf-email" data-testid="quote-field-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
      </div>
      <div>
        <label className={labelCls} htmlFor="qf-phone">Phone</label>
        <input id="qf-phone" data-testid="quote-field-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+1 ..." />
      </div>
      <div>
        <label className={labelCls} htmlFor="qf-whatsapp">WhatsApp</label>
        <input id="qf-whatsapp" data-testid="quote-field-whatsapp" className={inputCls} value={form.whatsapp} onChange={set("whatsapp")} placeholder="+1 ..." />
      </div>
      <div>
        <label className={labelCls} htmlFor="qf-category">Product Category</label>
        <select id="qf-category" data-testid="quote-field-category" className={inputCls} value={form.product_category} onChange={set("product_category")}>
          <option value="">Select category</option>
          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
      <div>
        <label className={labelCls} htmlFor="qf-product">Product</label>
        <select id="qf-product" data-testid="quote-field-product" className={inputCls} value={form.product} onChange={set("product")}>
          <option value="">Select product</option>
          {filtered.map((p) => <option key={p.slug} value={p.name}>{p.name}</option>)}
        </select>
      </div>
      <div>
        <label className={labelCls} htmlFor="qf-quantity">Required Quantity</label>
        <input id="qf-quantity" data-testid="quote-field-quantity" className={inputCls} value={form.quantity} onChange={set("quantity")} placeholder="e.g. 25 MT / 1 x 40ft container" />
      </div>
      <div>
        <label className={labelCls} htmlFor="qf-packaging">Packaging Requirement</label>
        <input id="qf-packaging" data-testid="quote-field-packaging" className={inputCls} value={form.packaging} onChange={set("packaging")} placeholder="e.g. 25 kg PP bags" />
      </div>
      <div>
        <label className={labelCls} htmlFor="qf-port">Destination Port</label>
        <input id="qf-port" data-testid="quote-field-port" className={inputCls} value={form.destination_port} onChange={set("destination_port")} placeholder="e.g. Jebel Ali, Dubai" />
      </div>
      <div>
        <label className={labelCls} htmlFor="qf-date">Target Delivery Date</label>
        <input id="qf-date" data-testid="quote-field-date" type="date" className={inputCls} value={form.target_date} onChange={set("target_date")} />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls} htmlFor="qf-message">Message</label>
        <textarea id="qf-message" data-testid="quote-field-message" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Share any additional requirements — specifications, certifications, timelines..." />
      </div>
      <label className="sm:col-span-2 flex items-start gap-3 text-sm text-[#4A554D] cursor-pointer" data-testid="quote-consent-label">
        <input type="checkbox" required checked={form.consent} onChange={set("consent")} data-testid="quote-field-consent" className="mt-1 h-4 w-4 accent-[#C69A43]" />
        I agree to be contacted regarding my inquiry.
      </label>
      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={sending}
          data-testid="quote-form-submit-button"
          className="inline-flex items-center gap-3 bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-parchment transition-colors duration-300 hover:bg-forest-leaf disabled:opacity-60"
        >
          {sending && <Loader2 className="h-4 w-4 animate-spin" />}
          {sending ? "Sending..." : "Submit Quote Request"}
        </button>
      </div>
    </form>
  );
}
